import { useState } from "react"

import { Alert, Divider, Flex } from "@/atoms"
import { getDetailMessage } from "@/helpers/error.helper"
import { useTranslation } from "@/hooks/useTranslation"
import { EyeOutlined, FileProtectOutlined, StopOutlined } from "@/icons"
import { Form } from "@/organisms/Form"
import {
  hasAccessWithoutToolName,
  hasDestinationsOffNetwork,
  hasSensorAndPlatformConditions
} from "@/pages/GovernancePage/applications/ai-policies/liveRuleGroups.selector"
import {
  getAiPolicyFormValues,
  hasCondition
} from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies.selector"
import { useAiPolicies } from "@/pages/GovernancePage/applications/policies/useAiPolicies"
import { LiveMcpScopeResources } from "@/pages/GovernancePage/components/organisms/LiveMcpScopeResources"
import { LiveRuleField } from "@/pages/GovernancePage/components/organisms/LiveRuleField"
import { LiveScopeResources } from "@/pages/GovernancePage/components/organisms/LiveScopeResources"
import { ENFORCEMENT_SECTION_ID } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/EnforcementSection"
import { RuleTitle } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/RuleTitle"
import { SummaryPolicy } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/SummaryPolicy"
import { LiveEnforcementSection } from "../LiveEnforcementSection"

import styles from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/AiPolicyForm/AiPolicyForm.module.scss"

import type { AiPolicyFormValues } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/AiPolicyForm/AiPolicyForm.types"
import type { LiveAiPolicyFormProps } from "./LiveAiPolicyForm.types"

const RULES = "notAllow"

// A real policy's form: deny rules only, then what happens on a violation
export const LiveAiPolicyForm = (props: LiveAiPolicyFormProps) => {
  const { record, redirectUrl, setUpdating } = props
  const { live } = useAiPolicies()
  const { t } = useTranslation()
  const { t: tForm } = useTranslation("translation", {
    keyPrefix: "governance.ai.form"
  })
  const { form, message } = Form.useForm()
  const [error, setError] = useState<string>()
  const isMcpServer = live?.kind === "mcpServer"

  const save = async (values: AiPolicyFormValues) => {
    if (!live) return

    setError(undefined)
    setUpdating?.(true)

    try {
      await live.save(values, record)
      message.onSuccess(values.name_details, {})
    } catch (err) {
      setError(getDetailMessage(err) || t("error.serverError"))

      throw err
    } finally {
      setUpdating?.(false)
    }
  }

  return (
    <Form<AiPolicyFormValues>
      initialValues={getAiPolicyFormValues(record?.live, live?.kind)}
      navigateOnFinished={redirectUrl}
      onValuesChange={(changed: Partial<AiPolicyFormValues>) => {
        if (!(RULES in changed)) return

        // A list's own rules, and a card's, run again only when an item is
        // added or removed: a shown error goes once its rules change
        const shown = [
          [RULES],
          ...(form.getFieldValue(RULES) ?? []).map((_: unknown, i: number) => [
            RULES,
            i,
            "rules"
          ])
        ].filter((field) => form.getFieldError(field).length)

        if (shown.length) form.validateFields(shown).catch(() => undefined)
      }}
      onFinish={save}
    >
      {error && <Alert showIcon type="error" title={error} />}
      <Form.Section.Details
        prefix={
          record && (
            <>
              <SummaryPolicy record={record} />
              <Divider className={styles.divider16} />
            </>
          )
        }
        title="AI Policy Details"
        label="AI Policy Name"
      />
      <Form.Section.Resources
        form={form}
        Icon={EyeOutlined}
        keyPrefix={
          isMcpServer
            ? "governance.ai.live.form.mcpResources"
            : "governance.ai.form.resources"
        }
      >
        {isMcpServer ? (
          <LiveMcpScopeResources saved={record?.live?.scope?.mcpServerNames} />
        ) : (
          <LiveScopeResources form={form} tForm={tForm} />
        )}
      </Form.Section.Resources>
      <Form.Section.Template
        id="rules"
        title="Rules"
        Icon={FileProtectOutlined}
      >
        <Flex vertical gap={14} className={styles.rulesSection}>
          <RuleTitle type={RULES} kind={live?.kind} />
          <Form.List
            name={RULES}
            rules={[
              {
                validator: async (_, groups) => {
                  if (hasAccessWithoutToolName(groups))
                    throw new Error(
                      t("governance.ai.live.form.rule.accessWithoutToolName")
                    )
                  if (hasSensorAndPlatformConditions(groups))
                    throw new Error(
                      t("governance.ai.live.form.rule.sensorAndPlatform")
                    )
                  if (hasDestinationsOffNetwork(groups))
                    throw new Error(
                      t("governance.ai.live.form.rule.destinationsOffNetwork")
                    )
                  if (!hasCondition(groups))
                    throw new Error(t("governance.ai.live.form.rule.required"))
                }
              }
            ]}
          >
            {(fields, listActions, { errors }) => (
              <>
                {fields.length === 0 && (
                  <LiveRuleField
                    name={0}
                    type={RULES}
                    listActions={listActions}
                  />
                )}
                {fields.map(({ key, name }) => (
                  <LiveRuleField
                    key={key}
                    name={name}
                    type={RULES}
                    listActions={listActions}
                  />
                ))}
                <Form.ErrorList errors={errors} />
              </>
            )}
          </Form.List>
        </Flex>
      </Form.Section.Template>
      <Form.Section.Template
        id={ENFORCEMENT_SECTION_ID}
        title={t("governance.ai.live.form.enforcement.title")}
        Icon={StopOutlined}
      >
        <div className={styles.rulesSection}>
          <LiveEnforcementSection />
        </div>
      </Form.Section.Template>
    </Form>
  )
}
